/**
 * API utility functions for Retrosive
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Get authorization headers with token
 */
export const getAuthHeaders = (token?: string): HeadersInit => {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Handle API response errors
 */
export const handleApiError = async (response: Response): Promise<never> => {
  let errorMessage = 'An error occurred';

  try {
    const errorData = await response.json();
    errorMessage = errorData.error || errorData.message || errorData.detail || errorMessage;

    // Handle rate limit errors
    if (response.status === 429) {
      const retryAfter = errorData.retry_after || response.headers.get('Retry-After') || '60';
      errorMessage = `${errorMessage} (Retry after ${retryAfter} seconds)`;
    }
  } catch (e) {
    // If response is not JSON, use status text
    errorMessage = response.statusText || errorMessage;
  }

  throw new Error(errorMessage);
};

/**
 * Fetch board templates
 */
export const fetchBoardTemplates = async (category?: string) => {
  const url = category
    ? `${API_BASE_URL}/board-templates/?category=${category}`
    : `${API_BASE_URL}/board-templates/`;

  const response = await fetch(url);

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Export board data
 */
export const exportBoard = async (
  boardId: string,
  format: 'json' | 'csv' | 'pdf',
  token: string,
  includeVotes: boolean = true
): Promise<Blob> => {
  const url = `${API_BASE_URL}/boards/${boardId}/export/?format=${format}&include_votes=${includeVotes}`;

  const response = await fetch(url, {
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.blob();
};

/**
 * Download file from blob
 */
export const downloadFile = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Group cards together
 */
export const groupCards = async (
  cardId: string,
  targetCardIds: string[],
  token: string
) => {
  const url = `${API_BASE_URL}/cards/${cardId}/group/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ target_card_ids: targetCardIds }),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Ungroup a card
 */
export const ungroupCard = async (cardId: string, token: string) => {
  const url = `${API_BASE_URL}/cards/${cardId}/ungroup/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Get grouped cards
 */
export const getGroupedCards = async (cardId: string, token: string) => {
  const url = `${API_BASE_URL}/cards/${cardId}/group_cards/`;

  const response = await fetch(url, {
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

// Timer API functions

/**
 * Get board timer
 */
export const getBoardTimer = async (boardId: string, token: string) => {
  const url = `${API_BASE_URL}/boards/${boardId}/timer/`;

  const response = await fetch(url, {
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Update board timer duration
 */
export const updateBoardTimer = async (
  boardId: string,
  durationMinutes: number,
  token: string
) => {
  const url = `${API_BASE_URL}/boards/${boardId}/timer/`;

  const response = await fetch(url, {
    method: 'PUT',
    headers: getAuthHeaders(token),
    body: JSON.stringify({ duration_minutes: durationMinutes }),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Start board timer
 */
export const startBoardTimer = async (boardId: string, token: string) => {
  const url = `${API_BASE_URL}/boards/${boardId}/timer/start/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Pause board timer
 */
export const pauseBoardTimer = async (boardId: string, token: string) => {
  const url = `${API_BASE_URL}/boards/${boardId}/timer/pause/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Stop board timer
 */
export const stopBoardTimer = async (boardId: string, token: string) => {
  const url = `${API_BASE_URL}/boards/${boardId}/timer/stop/`;

  const response = await fetch(url, {
    method: 'POST',
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

// ---------------------------------------------------------------------------
// AI Report API
// ---------------------------------------------------------------------------

export interface AiEntitlement {
  plan: string;
  limit: number | null;
  used: number | null;
  remaining: number | null;
  resetAt: string;
  isUnlimited: boolean;
}

export interface AiReportCardNote {
  note: string;
  translation?: string;
}

export interface AiReportActionItem {
  title: string;
  owner: string | null;
  due_date: string | null;
  notes?: string;
}

export interface AiReportRecommendation {
  recommendation: string;
  rationale?: string;
}

export interface AiReport {
  retro_name?: string;
  retro_date?: string;
  template?: string[];
  executive_summary: string;
  went_well?: AiReportCardNote[];
  to_improve?: AiReportCardNote[];
  action_items?: AiReportActionItem[];
  recommendations?: (string | AiReportRecommendation)[];
  risks?: string[];
  confidence_score?: number;
}

export interface AiReportResult {
  status: 'processing' | 'workflow_error';
  message: string;
  idempotency_key: string;
  quota: AiEntitlement;
  workflow?: Record<string, unknown>;
  report?: AiReport;
}

export class AiQuotaExceededError extends Error {
  plan: string;
  limit: number;
  used: number;
  remaining: number;
  resetAt: string;
  constructor(data: { plan: string; limit: number; used: number; remaining: number; resetAt: string; message: string }) {
    super(data.message);
    this.name = 'AiQuotaExceededError';
    this.plan = data.plan;
    this.limit = data.limit;
    this.used = data.used;
    this.remaining = data.remaining;
    this.resetAt = data.resetAt;
  }
}

/**
 * Fetch the current AI usage entitlement for the authenticated user's org.
 */
export const getAiEntitlement = async (token: string, organizationId?: string): Promise<AiEntitlement> => {
  const params = organizationId ? `?organization_id=${organizationId}` : '';
  const response = await fetch(`${API_BASE_URL}/subscription/billing/entitlement/${params}`, {
    headers: getAuthHeaders(token),
  });

  if (!response.ok) {
    await handleApiError(response);
  }

  return response.json();
};

/**
 * Trigger AI report generation for a board.
 * Throws AiQuotaExceededError (HTTP 402) if the org is over quota.
 */
export const generateAiReport = async (
  boardId: string,
  token: string,
  idempotencyKey?: string,
): Promise<AiReportResult> => {
  const body: Record<string, string> = { board_id: boardId };
  if (idempotencyKey) body.idempotency_key = idempotencyKey;

  const response = await fetch(`${API_BASE_URL}/subscription/ai/reports/`, {
    method: 'POST',
    headers: getAuthHeaders(token),
    body: JSON.stringify(body),
  });

  const data = await response.json();

  if (response.status === 402) {
    throw new AiQuotaExceededError({
      plan: data.plan,
      limit: data.limit,
      used: data.used,
      remaining: data.remaining,
      resetAt: data.resetAt,
      message: data.message || 'AI usage limit reached.',
    });
  }

  if (!response.ok) {
    throw new Error(data.message || data.error || 'Failed to generate AI report.');
  }

  // Parse the AI report from n8n's nested response structure.
  // n8n can return Claude's output in several shapes; we try each in order.
  let report: AiReport | undefined;
  try {
    const rawWorkflow = data.workflow;

    let text: string | undefined;

    // Shape A: top-level array  →  [{ output: [{ content: [{ text }] }] }]
    // This is what the Anthropic node returns when connected directly to Webhook.
    if (Array.isArray(rawWorkflow)) {
      const firstItem = rawWorkflow[0] as Record<string, unknown> | undefined;
      const outputArr = firstItem?.output as Array<Record<string, unknown>> | undefined;
      const contentArr = outputArr?.[0]?.content as Array<Record<string, unknown>> | undefined;
      text = contentArr?.[0]?.text as string | undefined;
      // fallback: output[0].text
      if (!text) text = outputArr?.[0]?.text as string | undefined;
    }

    if (!text && !Array.isArray(rawWorkflow) && rawWorkflow) {
      const wf = rawWorkflow as Record<string, unknown>;
      // Shape B: { output: [{ content: [{ text }] }] }
      const outputArr = wf.output as Array<Record<string, unknown>> | undefined;
      const contentArr = outputArr?.[0]?.content as Array<Record<string, unknown>> | undefined;
      text = contentArr?.[0]?.text as string | undefined;
      // Shape C: { output: [{ text }] }
      if (!text) text = outputArr?.[0]?.text as string | undefined;
      // Shape D: { text: "..." }
      if (!text) text = wf.text as string | undefined;
      // Shape E: already the report object
      if (!text && wf.executive_summary) report = wf as unknown as AiReport;
    }

    // Also check data.report if backend already extracted it
    if (!report && !text && data.report) {
      report = data.report as AiReport;
    }

    if (!report && text) {
      const stripped = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
      report = JSON.parse(stripped) as AiReport;
    }
  } catch (e) {
    console.warn('[AI Report] Failed to parse report from workflow response:', e);
  }

  return { ...data, report } as AiReportResult;
};

export interface AiReportHistoryItem {
  id: string;
  createdAt: string;
  report: AiReport;
}

export const getAiReportHistory = async (
  boardId: string,
  token: string,
): Promise<AiReportHistoryItem[]> => {
  const res = await fetch(
    `${API_BASE_URL}/subscription/ai/reports/history/?board_id=${boardId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) return [];
  const data = await res.json();
  return data.reports ?? [];
};

/**
 * Format time in MM:SS format
 */
export const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export interface AiInsightsSession {
  board_id: string | null;
  board_title: string;
  date: string;
  sentiment_label: string;
  sentiment_score: number | null;
}

export interface AiInsights {
  recurring_issues: string[];
  improving_areas: string[];
  unresolved_action_items: string[];
  sentiment_trend: { date: string; score: number; label: string }[];
  health_score: number | null;
  total_sessions: number;
  sessions: AiInsightsSession[];
}

export const getAiInsights = async (token: string): Promise<AiInsights> => {
  const res = await fetch(`${API_BASE_URL}/subscription/ai/insights/`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Failed to fetch AI insights');
  return res.json();
};

